/**
 * Pure itinerary-summary builder for the Itinerary modal — takes the outputs
 * of existing Vuex getters and shapes them into a printable day-by-day trip
 * summary. No store access, no side effects (mirrors stage-destinations.ts).
 */

import type { Route } from '../../../utils/models/Route';
import {
  AttachedStageDestination,
  AttachedTrailStart,
  StageDestination,
  stageEndForDay,
  stageStartForDay,
} from './stage-destinations';

export interface ItineraryDayEntry {
  dayNumber: number;
  name: string;
  description: string;
  distanceMeters: number;
  ascentMeters: number;
  durationSeconds: number;
  sunrise: Date | null;
  sunset: Date | null;
  startLabel: StageDestination | null;
  /** The day's start point, always populated when the route geometry has
   *  one — a fallback for the UI to show raw coordinates when startLabel
   *  is null (no POI/waypoint/highlight attached there). */
  startCoordinates: [number, number] | null;
  endLabel: StageDestination | null;
  /** Same fallback as startCoordinates, for the day's end point. */
  endCoordinates: [number, number] | null;
}

export interface ItineraryData {
  days: ItineraryDayEntry[];
  totalDistanceMeters: number;
  totalAscentMeters: number;
  totalDurationSeconds: number;
  tripStart: StageDestination | null;
  tripStartCoordinates: [number, number] | null;
  tripEnd: StageDestination | null;
  tripEndCoordinates: [number, number] | null;
}

interface ItineraryRoute {
  distance?: number;
  ascent?: number;
  route_style?: string;
}

export interface BuildItineraryParams {
  routes: { [routeKey: string]: Route };
  routesByDay: { [day: number]: ItineraryRoute[] };
  dayInfo: { [day: number]: { name?: string; description?: string } };
  dayStats: {
    [day: number]: {
      durationFastSeconds: number;
      sunrise: Date | null;
      sunset: Date | null;
    };
  };
  attachedStageDestinations: { [day: number]: AttachedStageDestination };
  attachedTrailStart: AttachedTrailStart | null;
  totalDistanceMeters: number;
  totalAscentMeters: number;
  totalDurationSeconds: number;
}

function isMainRoute(route: ItineraryRoute): boolean {
  return (route.route_style || 'route') === 'route';
}

export function buildItinerary(params: BuildItineraryParams): ItineraryData {
  const {
    routes,
    routesByDay,
    dayInfo,
    dayStats,
    attachedStageDestinations,
    attachedTrailStart,
    totalDistanceMeters,
    totalAscentMeters,
    totalDurationSeconds,
  } = params;

  const usedDays = Object.keys(routesByDay)
    .map(Number)
    .filter(day => (routesByDay[day]?.length || 0) > 0)
    .sort((a, b) => a - b);

  const tripStart = attachedTrailStart?.destination ?? null;
  // The single trip-start attachment can now live on ANY day's stage-start
  // (not just day 1's) — mirror getHikeDaysAttributes' convention exactly so
  // the day that's actually shown as attached here matches what gets saved.
  const trailStartDayNumber = attachedTrailStart
    ? (routes[attachedTrailStart.routeKey]?.day ?? 1)
    : null;
  const tripStartCoordinates = usedDays.length > 0
    ? stageStartForDay(routes, usedDays[0])?.coordinates ?? null
    : null;

  const days: ItineraryDayEntry[] = usedDays.map((dayNumber, index) => {
    const dayRoutes = routesByDay[dayNumber] || [];
    const mains = dayRoutes.filter(isMainRoute);
    const distanceMeters = mains.reduce((sum, route) => sum + (route.distance || 0), 0);
    const ascentMeters = mains.reduce((sum, route) => sum + (route.ascent || 0), 0);

    const stats = dayStats[dayNumber];
    const info = dayInfo[dayNumber];

    // If this day owns the (singular) trip-start attachment, show it here;
    // otherwise fall back to the previous used day's end destination when
    // the two stages are connected (same physical junction, shown once per
    // side) — same reasoning stage-destinations.ts documents for the reverse
    // remap. Neither applies → this day's start is genuinely unattached.
    const previousDayNumber = index > 0 ? usedDays[index - 1] : null;
    const startLabel = (dayNumber === trailStartDayNumber)
      ? tripStart
      : (previousDayNumber !== null ? attachedStageDestinations[previousDayNumber]?.destination ?? null : null);
    const startCoordinates = stageStartForDay(routes, dayNumber)?.coordinates ?? null;
    const endLabel = attachedStageDestinations[dayNumber]?.destination ?? null;
    const endCoordinates = stageEndForDay(routes, dayNumber)?.coordinates ?? null;

    return {
      dayNumber,
      name: info?.name || `Day ${dayNumber}`,
      description: info?.description || '',
      distanceMeters,
      ascentMeters,
      durationSeconds: stats?.durationFastSeconds || 0,
      sunrise: stats?.sunrise ?? null,
      sunset: stats?.sunset ?? null,
      startLabel,
      startCoordinates,
      endLabel,
      endCoordinates,
    };
  });

  const tripEnd = days.length > 0 ? days[days.length - 1].endLabel : null;
  const tripEndCoordinates = days.length > 0 ? days[days.length - 1].endCoordinates : null;

  return {
    days,
    totalDistanceMeters,
    totalAscentMeters,
    totalDurationSeconds,
    tripStart,
    tripStartCoordinates,
    tripEnd,
    tripEndCoordinates,
  };
}
